import KeyboardEvent from '../../event/js/keyboard';

import * as globals from './globals';

function getPositionX(event) {
  // check if touch event, otherwise mouse event
  if (event.changedTouches && !!event.changedTouches.length) {
    return event.changedTouches[0].pageX;
  }

  return event.pageX;
}

class Timetable {
  constructor(tableElement) {
    this.tableElement = tableElement;
    this.timeColumnElements = tableElement.querySelectorAll(globals.TIME_COLUMN_SELECTOR);
    this.navigationElement = tableElement.querySelector(globals.NAVIGATION_SELECTOR);
    this.prevButtonElement = tableElement.querySelector(globals.NAVIGATION_PREV_BUTTON_SELECTOR);
    this.nextButtonElement = tableElement.querySelector(globals.NAVIGATION_NEXT_BUTTON_SELECTOR);
    this.tableHeaderTitleElement = tableElement.querySelector(globals.HEADER_TITLE_SELECTOR);
    this.tableContentElement = tableElement.querySelector(globals.CONTENT_SELECTOR);
    this.rowElements = tableElement.querySelectorAll(globals.ROW_SELECTOR);
    this.timeColumnElement = this.timeColumnElements[0];

    this.stopColumnElements = tableElement.querySelectorAll(globals.COL_STOP_SELECTOR);
    this.stopColumnElements.forEach((stopColumnElement) => {
      const cellElements = Array.from(stopColumnElement.nextElementSibling.children);
      const timeString = cellElements.filter((item) => item.innerText.trim() !== globals.NO_STOP_TEXT).map((item) => item.innerText).join(', ');
      stopColumnElement.querySelector('.at-sr-only').setAttribute('aria-label', `Time ${timeString}`);
    });

    const cellElements = this.timeColumnElement.querySelectorAll(globals.CELL_SELECTOR);
    this.cellsWidth = Array.from(cellElements).reduce((acc, cur) => acc + cur.offsetWidth, 0);
    this.tableElement.style = `--at-timetable--time-cell-amount: ${cellElements.length}`;
    this.scrollLeft = this.timeColumnElement.scrollLeft;

    this.isDownEvent = false;
    this.isMoveEvent = false;
    this.savedPositionX = 0;

    this.calculateWidth();
    window.addEventListener('resize', () => this.calculateWidth());

    this.prevButtonElement.addEventListener('click', () => this.scrollContent(globals.DIRECTION.PREV));
    this.nextButtonElement.addEventListener('click', () => this.scrollContent(globals.DIRECTION.NEXT));

    this.tableContentElement.addEventListener('mouseup', (event) => this.handleUpEvent(event));
    this.tableContentElement.addEventListener('mousedown', (event) => this.handleDownEvent(event));
    this.tableContentElement.addEventListener('mousemove', (event) => this.handleMoveEvent(event));

    this.timeColumnElements.forEach((element) => {
      element.addEventListener('touchend', (event) => this.handleUpEvent(event));
      element.addEventListener('touchstart', (event) => this.handleDownEvent(event));
      element.addEventListener('touchmove', (event) => this.handleMoveEvent(event));
    });

    this.tableContentElement.addEventListener('keydown', (userEvent) => this.handleKeyPressed(userEvent));
  }

  handleKeyPressed(userEvent) {
    if (!this.showNavigation) {
      return;
    }

    switch (true) {
      case KeyboardEvent.CheckKey(userEvent, KeyboardEvent.Key.LEFT):
        userEvent.preventDefault();
        this.scrollContent(globals.DIRECTION.PREV);
        break;
      case KeyboardEvent.CheckKey(userEvent, KeyboardEvent.Key.RIGHT):
        userEvent.preventDefault();
        this.scrollContent(globals.DIRECTION.NEXT);
        break;
      default:
        break;
    }
  }

  handleUpEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = false;
    this.isMoveEvent = false;

    if (this.savedPositionX - getPositionX(event) < -globals.SWIPE_THRESHOLD) {
      this.scrollContent(globals.DIRECTION.PREV);
    }

    if (this.savedPositionX - getPositionX(event) > globals.SWIPE_THRESHOLD) {
      this.scrollContent(globals.DIRECTION.NEXT);
    }
  }

  handleDownEvent(event) {
    if (!event.cancelable) {
      return;
    }

    event.preventDefault();

    this.isDownEvent = true;
    this.savedPositionX = getPositionX(event);
  }

  handleMoveEvent(event) {
    if (!event.cancelable && !this.isDownEvent) {
      return;
    }

    event.preventDefault();

    this.isMoveEvent = true;
  }

  updateNavigation() {
    if (!this.showNavigation) {
      this.scrollLeft = 0;
      this.timeColumnElements.forEach((element) => element.classList.remove(globals.SHADOW_EARLIER_CLASS));
      this.tableContentElement.classList.remove(globals.SHADOW_LATER_CLASS);
      return;
    }

    if (this.scrollLeft <= 0) {
      this.prevButtonElement.setAttribute('disabled', true);
      this.timeColumnElements.forEach((element) => element.classList.remove(globals.SHADOW_EARLIER_CLASS));
    } else {
      this.prevButtonElement.removeAttribute('disabled');
      this.timeColumnElements.forEach((element) => element.classList.add(globals.SHADOW_EARLIER_CLASS));
    }

    if (this.scrollLeft >= this.timeColumnElement.scrollWidth - this.timeColumnElement.clientWidth) {
      this.nextButtonElement.setAttribute('disabled', true);
      this.rowElements.forEach((rowElement) => rowElement.classList.remove(globals.SHADOW_LATER_CLASS));
    } else {
      this.nextButtonElement.removeAttribute('disabled');
      this.rowElements.forEach((rowElement) => rowElement.classList.add(globals.SHADOW_LATER_CLASS));
    }
  }

  calculateWidth() {
    const firstHeaderElement = this.tableElement.querySelector('.at-timetable__first-header');
    const firstHeaderRect = firstHeaderElement.getBoundingClientRect();
    const firstHeaderWidth = firstHeaderRect.right - firstHeaderRect.left;

    let rowContentWidth = Math.round(this.tableElement.clientWidth - firstHeaderWidth - 1);

    if (rowContentWidth >= this.cellsWidth) {
      rowContentWidth = this.cellsWidth;
      this.showNavigation = false;
      this.navigationElement.style.display = 'none';
      this.tableHeaderTitleElement.classList.add('at-border-radius-top-left--rounded', 'at-border-radius-top-right--rounded');
    } else {
      this.showNavigation = true;
      this.navigationElement.style.display = 'flex';
      this.tableHeaderTitleElement.classList.remove('at-border-radius-top-left--rounded', 'at-border-radius-top-right--rounded');
    }

    this.stopColumnElements.forEach((element) => {
      const { style } = element;
      style.width = `${firstHeaderWidth}px`;
    });

    this.updateNavigation();
  }

  scrollContent(direction) {
    let newScrollLeftValue = direction === globals.DIRECTION.NEXT
      ? this.timeColumnElement.scrollWidth - this.timeColumnElement.clientWidth
      : 0;

    const cellElement = this.timeColumnElement.querySelector(globals.CELL_SELECTOR);

    if (direction === globals.DIRECTION.NEXT) {
      while (newScrollLeftValue - cellElement.clientWidth > this.scrollLeft) {
        newScrollLeftValue -= cellElement.clientWidth;
      }
    } else {
      while (newScrollLeftValue + cellElement.clientWidth < this.scrollLeft) {
        newScrollLeftValue += cellElement.clientWidth;
      }
    }

    this.timeColumnElements.forEach((element) => {
      element.scroll({ left: newScrollLeftValue, behavior: 'smooth' });
    });

    this.scrollLeft = newScrollLeftValue;

    this.updateNavigation();
  }
}

const initializeTimetable = () => {
  document.querySelectorAll(globals.TIMETABLE_DATA_ELEMENT).forEach((timetable) => new Timetable(timetable));
};

export {
  Timetable,
  initializeTimetable,
};